import { decode } from "@msgpack/msgpack/dist";

/**
 * Creates websocket payload handler for series download transport protocols.
 * @param {Object} deps
 * @returns {(message: MessageEvent, error?: Error|string) => Promise<void>}
 */
const createOnReceiveSeriesDownloadDataHandler = ({
  router,
  decrementInFlightDownloads,
  decrementInFlightDownloadsBy,
  processDownloadQueue,
  emitFatalSocketPayloadError,
  DOWNLOAD_TRANSPORT_MODE,
  pendingChunks,
  settleSeriesItemWithoutParse,
  settleBatchItemsWithoutParse,
  setSeriesItemInFlight,
  setSeriesItemParseQueued,
  seriesStateTrackers,
  maybeFinalizeSeries,
  toUint8Array,
  transitionSeriesItemState,
  ITEM_STATES,
  downloadPerf,
  store,
  downloadCounts,
  getInFlightDownloads,
  parseQueue,
  enqueueParseTask,
  drainParseQueue,
  getDeclaredSliceIndexForSeries,
  markSeriesItemReceived,
  enqueueParseBytesTask
}) => {
  const commitPayloadAnnotations = (dbImageId, annotations) => {
    if (!annotations || annotations.length <= 0) {
      return;
    }
    store.commit("viewer/setAnnotations", {
      imageId: dbImageId,
      annotations
    });
  };

  const handleInlinePayloadItem = ({
    item,
    chunkIndex = 0,
    onInvalidPayload
  } = {}) => {
    const {
      db_id,
      image_id,
      series_id,
      element_id,
      data: payloadData,
      annotations,
      total_size
    } = item || {};

    commitPayloadAnnotations(db_id, annotations);
    const fullArray = toUint8Array(payloadData);
    if (!fullArray) {
      if (typeof onInvalidPayload === "function") {
        onInvalidPayload({ seriesId: series_id, dbImageId: db_id, chunkIndex });
      }
      return false;
    }

    markSeriesItemReceived({
      seriesId: series_id,
      dbImageId: db_id,
      payloadSize: total_size || fullArray.length,
      chunkIndex
    });
    enqueueParseBytesTask({
      seriesId: series_id,
      elementId: element_id,
      dbImageId: db_id,
      byteArray: fullArray,
      fileName: image_id
    });
    return true;
  };

  /**
   * Handles one websocket frame and routes it by transport payload type.
   * @param {MessageEvent} message
   * @param {Error|string} [error]
   * @returns {Promise<void>}
   */
  return async (message, error) => {
    // Parsing/rendering work is only allowed in viewer routes; elsewhere we only settle counters.
    const isViewerRoute =
      router.history.current.name === "viewer" ||
      router.history.current.name === "share";

    if (!(message.data instanceof Blob)) {
      if (isViewerRoute) {
        emitFatalSocketPayloadError({
          error,
          userMessage: error || "Failed to download series: no data received",
          perfStage: "ws_non_blob_payload"
        });
        return;
      }
      decrementInFlightDownloads();
      return;
    }

    let packedBuffer;
    try {
      packedBuffer = await message.data.arrayBuffer();
    } catch (arrayBufferError) {
      if (isViewerRoute) {
        console.error("Failed to read WS DICOM payload", arrayBufferError);
        emitFatalSocketPayloadError({
          error: arrayBufferError,
          userMessage: "Failed to read downloaded DICOM payload",
          perfStage: "ws_array_buffer"
        });
        return;
      }
      decrementInFlightDownloads();
      return;
    }

    let msg;
    try {
      msg = decode(packedBuffer);
    } catch (decodeError) {
      if (isViewerRoute) {
        console.error("Failed to decode WS DICOM payload", decodeError);
        emitFatalSocketPayloadError({
          error: decodeError,
          userMessage: "Failed to decode downloaded DICOM payload",
          perfStage: "ws_decode"
        });
        return;
      }
      decrementInFlightDownloads();
      return;
    }

    if (!isViewerRoute) {
      if (msg && msg.type === "chunk") {
        if (msg.is_last) {
          delete pendingChunks[msg.db_id];
          settleSeriesItemWithoutParse(
            msg.series_id,
            msg.db_id,
            "non_viewer_chunk_last"
          );
        }
        return;
      }

      if (msg && msg.type === DOWNLOAD_TRANSPORT_MODE.SINGLE) {
        settleSeriesItemWithoutParse(
          msg.series_id,
          msg.db_id,
          "non_viewer_single_payload"
        );
        return;
      }

      if (msg && msg.type === DOWNLOAD_TRANSPORT_MODE.BATCH) {
        const items = Array.isArray(msg.items) ? msg.items : [];
        if (!items.length) {
          const expectedItems = Number.isFinite(msg.items_count)
            ? Math.max(1, Math.floor(msg.items_count))
            : 1;
          decrementInFlightDownloadsBy(expectedItems);
          return;
        }
        settleBatchItemsWithoutParse(items, "non_viewer_batch_payload");
        return;
      }

      decrementInFlightDownloads();
      return;
    }

    // Chunk messages accumulate bytes progressively and enqueue parse only on the final chunk.
    if (msg && msg.type === "chunk") {
      const {
        db_id,
        image_id,
        series_id,
        element_id,
        chunk_index,
        is_last,
        data: chunkData,
        annotations,
        total_size
      } = msg;

      if (chunk_index === 0) {
        pendingChunks[db_id] = {
          chunks: [],
          received: 0,
          totalSize: total_size ?? null,
          seriesId: series_id,
          elementId: element_id,
          imageId: image_id,
          annotations: annotations ?? []
        };
        downloadPerf.markFirstChunk({
          seriesId: series_id,
          dbImageId: db_id,
          totalSize: total_size
        });
        commitPayloadAnnotations(db_id, annotations);
      }

      const pending = pendingChunks[db_id];
      if (!pending) {
        if (is_last) {
          setSeriesItemInFlight(seriesStateTrackers[series_id], db_id, false);
          setSeriesItemParseQueued(seriesStateTrackers[series_id], db_id, false);
          decrementInFlightDownloads();
          maybeFinalizeSeries(series_id, "orphan_last_chunk");
          processDownloadQueue();
        }
        return;
      }

      if (chunkData && chunkData.length > 0) {
        const chunk = toUint8Array(chunkData);
        pending.chunks.push(chunk);
        pending.received += chunk.length;
        downloadPerf.markChunk({
          seriesId: pending.seriesId,
          dbImageId: db_id,
          bytes: chunk.length,
          chunkIndex: chunk_index,
          inFlight: getInFlightDownloads(),
          parseQueueLength: parseQueue.length,
          pendingChunkCount: Object.keys(pendingChunks).length
        });
      }

      if (!is_last) {
        if (pending.totalSize) {
          const counts = downloadCounts[pending.seriesId];
          if (counts) {
            const alreadyLoaded = counts.loaded;
            const totalImages = counts.total;
            const imageFraction = pending.received / pending.totalSize;
            const overallValue = Math.round(
              ((alreadyLoaded + imageFraction) / totalImages) * 100
            );
            store.commit("viewer/setDownloadProgress", {
              seriesId: pending.seriesId,
              value: overallValue,
              loaded: alreadyLoaded,
              total: totalImages
            });
          }
        }
        return;
      }

      delete pendingChunks[db_id];
      transitionSeriesItemState(
        seriesStateTrackers[pending.seriesId],
        db_id,
        ITEM_STATES.RECEIVED
      );
      downloadPerf.markLastChunk({
        seriesId: pending.seriesId,
        dbImageId: db_id,
        parseQueueLength: parseQueue.length
      });
      downloadPerf.markItemState({
        seriesId: pending.seriesId,
        dbImageId: db_id,
        state: ITEM_STATES.RECEIVED
      });

      const totalLen = pending.chunks.reduce((s, c) => s + c.length, 0);
      const fullArray = new Uint8Array(totalLen);
      let offset = 0;
      for (const chunk of pending.chunks) {
        fullArray.set(chunk, offset);
        offset += chunk.length;
      }

      enqueueParseTask({
        seriesId: pending.seriesId,
        elementId: pending.elementId,
        dbImageId: db_id,
        sliceIndex: getDeclaredSliceIndexForSeries(pending.seriesId, db_id),
        byteArray: fullArray,
        fileName: pending.imageId
      });

      drainParseQueue();
      return;
    }

    if (msg && msg.type === DOWNLOAD_TRANSPORT_MODE.SINGLE) {
      const enqueued = handleInlinePayloadItem({
        item: msg,
        chunkIndex: 0,
        onInvalidPayload: ({ seriesId, dbImageId }) => {
          decrementInFlightDownloads();
          processDownloadQueue();
          downloadPerf.recordError({
            seriesId: seriesId || null,
            dbImageId: dbImageId || null,
            stage: "single_payload_decode",
            message: "Invalid single payload data"
          });
          store.dispatch("raiseSnackbar", {
            text: "Failed to download series: invalid single payload",
            color: "error",
            timeout: -1
          });
        }
      });
      if (!enqueued) {
        return;
      }
      drainParseQueue();
      return;
    }

    if (msg && msg.type === DOWNLOAD_TRANSPORT_MODE.BATCH) {
      const items = Array.isArray(msg.items) ? msg.items : [];
      if (!items.length) {
        const expectedItems = Number.isFinite(msg.items_count)
          ? Math.max(1, Math.floor(msg.items_count))
          : 1;
        decrementInFlightDownloadsBy(expectedItems);
        processDownloadQueue();
        downloadPerf.recordError({
          seriesId: msg?.series_id || null,
          stage: "batch_payload_empty",
          message: "Batch payload has no items"
        });
        store.dispatch("raiseSnackbar", {
          text: "Failed to download series: empty batch payload",
          color: "error",
          timeout: -1
        });
        return;
      }

      let enqueuedItems = 0;
      items.forEach((item, itemIndex) => {
        const enqueued = handleInlinePayloadItem({
          item,
          chunkIndex: itemIndex,
          onInvalidPayload: ({ seriesId, dbImageId }) => {
            downloadPerf.recordError({
              seriesId: seriesId || null,
              dbImageId: dbImageId || null,
              stage: "batch_item_payload_decode",
              message: `Invalid batch item payload at index ${itemIndex}`
            });
            settleSeriesItemWithoutParse(
              seriesId,
              dbImageId,
              "batch_item_payload_decode_error"
            );
          }
        });
        if (enqueued) {
          enqueuedItems += 1;
        }
      });

      if (enqueuedItems > 0) {
        drainParseQueue();
      } else {
        processDownloadQueue();
      }
      return;
    }

    decrementInFlightDownloads();
    processDownloadQueue();
    console.error("Received unexpected WS message format", msg);
    downloadPerf.recordError({
      seriesId: msg?.series_id || null,
      dbImageId: msg?.db_id || null,
      stage: "unexpected_ws_payload",
      message: "Unexpected payload format"
    });
    store.dispatch("raiseSnackbar", {
      text: "Failed to download series: unexpected payload format",
      color: "error",
      timeout: -1
    });
  };
};

export { createOnReceiveSeriesDownloadDataHandler };
